import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { fetchPlayerById, fetchPlayerDiscovery, recomputePlayerStatsById, searchPlayerDiscovery } from '../../../services/playerService';
import {
  fetchFollowCounts,
  fetchFollowStatus,
  followUser,
  unfollowUser,
  upsertCaptainRating,
  upsertPlayerRating,
} from '../../../services/socialService';
import { normalizeProfile } from '../../../utils/profile';
import PlayerPreview from '../../../components/sections/PlayerPreview';
import { PlayerCard } from '../../../components/common/cards';

const discoveryCardProfile = (player = {}) => ({
  ...player,
  _id: player.id || player._id,
  personal: { fullName: player.fullName, city: player.city, country: player.country },
  skills: { primaryRole: player.primaryRole },
  media: { profilePhoto: player.profilePhoto },
});

const PlayersDirectory = ({
  fetchList,
  fetchDetail = fetchPlayerById,
  listTitle = 'Discover Players',
  listSubtitle = 'Your cricket network',
  listDescription = 'Find players from matches, Tournaments, and your city—or look up someone specific.',
  detailTitle = 'Profile overview',
  detailDescription = 'Review the full profile for the selected player.',
}) => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { playerId } = useParams();
  const [players, setPlayers] = useState([]);
  const [discoverySections, setDiscoverySections] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [followData, setFollowData] = useState({ isFollowing: false, followersCount: 0, followingCount: 0 });
  const [playerRating, setPlayerRating] = useState({ visibility: '', averageRating: 0, ratingsCount: 0, myRating: null, ratingsReceived: [] });
  const [captainRating, setCaptainRating] = useState({ visibility: '', averageRating: 0, ratingsCount: 0, myRating: null, ratingsReceived: [], eligible: false });
  const [ratingPrivacyMessage, setRatingPrivacyMessage] = useState('');
  const [isLoadingSocial, setIsLoadingSocial] = useState(false);
  const [showRatingEditor, setShowRatingEditor] = useState(false);
  const [isRecomputingStats, setIsRecomputingStats] = useState(false);

  useEffect(() => {
    if (playerId) {
      return undefined;
    }

    const loadPlayers = async () => {
      setIsLoadingList(true);
      setStatusMessage('');
      try {
        if (fetchList) {
          const playerList = await fetchList(token);
          setPlayers(Array.isArray(playerList) ? playerList : playerList.players || []);
          setDiscoverySections([]);
        } else {
          const payload = await fetchPlayerDiscovery(token);
          const labels = { playedWith: 'Played With You', tournaments: 'Your Tournaments', nearby: 'Nearby Players' };
          const seen = new Set();
          const sections = Object.entries(labels).map(([key, title]) => ({
            key,
            title,
            players: (payload?.sections?.[key] || []).filter((player) => {
              const id = String(player.id || player._id || '');
              if (!id || seen.has(id)) return false;
              seen.add(id);
              return true;
            }),
          }));
          setDiscoverySections(sections);
          setPlayers(sections.flatMap((section) => section.players));
        }
      } catch (error) {
        setStatusMessage(error.message || 'Unable to load players.');
      } finally {
        setIsLoadingList(false);
      }
    };

    loadPlayers();
    return undefined;
  }, [fetchList, playerId, token]);

  useEffect(() => {
    if (fetchList || playerId) return undefined;
    const query = searchTerm.trim();
    if (!query) return undefined;
    const timer = setTimeout(async () => {
      setIsLoadingList(true);
      try {
        const result = await searchPlayerDiscovery(query, token);
        setPlayers((result.players || []).map(discoveryCardProfile));
        setDiscoverySections([]);
        setStatusMessage(result.exactRequired ? 'Enter the complete mobile number or email address.' : '');
      } catch (error) {
        setStatusMessage(error.message || 'Unable to search players.');
      } finally {
        setIsLoadingList(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [fetchList, playerId, searchTerm, token]);

  useEffect(() => {
    if (!playerId) {
      setSelectedPlayer(null);
      return;
    }

    const loadPlayerDetail = async () => {
      setIsLoadingDetail(true);
      setStatusMessage('');
      try {
        const playerDetail = await fetchDetail(playerId, token);
        setSelectedPlayer(normalizeProfile(playerDetail));
      } catch (error) {
        setStatusMessage(error.message || 'Unable to load player details.');
      } finally {
        setIsLoadingDetail(false);
      }
    };

    loadPlayerDetail();
  }, [fetchDetail, playerId, token]);

  useEffect(() => {
    if (playerId) {
      setIsLoadingSocial(false);
    }
  }, [playerId]);

  const submitRating = async (type, rating) => {
    const userId = selectedPlayer?.user;
    if (!token || !userId) return;
    setRatingPrivacyMessage('');
    if (type === 'player') {
      const response = await upsertPlayerRating(userId, { rating }, token);
      if (response?.message) setRatingPrivacyMessage(response.message);
      setPlayerRating((prev) => ({ ...prev, myRating: rating, visibility: 'rater' }));
      return;
    }

    const response = await upsertCaptainRating(userId, { rating }, token);
    if (response?.message) setRatingPrivacyMessage(response.message);
    setCaptainRating((prev) => ({ ...prev, myRating: rating, visibility: 'rater' }));
  };

  const toggleFollow = async () => {
    const userId = selectedPlayer?.user;
    if (!token || !userId) return;
    if (followData.isFollowing) {
      await unfollowUser(userId, token);
    } else {
      await followUser(userId, token);
    }
    const [status, counts] = await Promise.all([fetchFollowStatus(userId, token), fetchFollowCounts(userId)]);
    setFollowData({ ...counts, isFollowing: status.isFollowing });
  };


  const handleRecomputeStats = async () => {
    if (!token || !playerId) return;

    setIsRecomputingStats(true);
    setStatusMessage('');
    try {
      const response = await recomputePlayerStatsById(playerId, token);
      const updatedPlayer = response?.player || response?.updated || response?.profile || null;
      if (updatedPlayer) {
        setSelectedPlayer(normalizeProfile(updatedPlayer));
      }
      setStatusMessage('Player stats recomputed successfully.');
    } catch (error) {
      setStatusMessage(error.message || 'Unable to recompute player stats.');
    } finally {
      setIsRecomputingStats(false);
    }
  };

  const filteredPlayers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term || !fetchList) return players;

    return players.filter((player) => {
      const name = player.personal?.fullName || '';
      const city = player.personal?.city || '';
      const role = player.skills?.primaryRole || '';
      return [name, city, role].some((value) => value.toLowerCase().includes(term));
    });
  }, [fetchList, players, searchTerm]);

  const handleCardClick = (selectedId) => {
    navigate(`/players/${selectedId}`);
    setShowRatingEditor(false);
    setRatingPrivacyMessage('');
  };

  const handleReturnToList = () => {
    navigate('/players');
    setStatusMessage('');
    setShowRatingEditor(false);
    setRatingPrivacyMessage('');
  };

  const isOwnerView = Boolean(user?._id && selectedPlayer?.user && user._id === selectedPlayer.user);

  return (
    <div className="row g-4">
      {!playerId ? (
        <div className="col-12">
          <div className="card shadow-sm app-card">
            <div className="card-body d-flex flex-column gap-3">
              <div>
                <p className="eyebrow text-uppercase text-primary fw-semibold mb-1 small">{listSubtitle}</p>
                <h2 className="h4 mb-1">{listTitle}</h2>
                <p className="text-muted mb-0">{listDescription}</p>
              </div>

              <div className="d-flex flex-column flex-md-row gap-2">
                <input
                  className="form-control"
                  placeholder={fetchList ? 'Search by name, city, or role' : 'Search by name, exact mobile or exact email'}
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>

              {isLoadingList ? (
                <p className="text-muted mb-0">Loading players...</p>
              ) : discoverySections.length && !searchTerm.trim() ? (
                <div className="d-flex flex-column gap-4">
                  {discoverySections.map((section) => (
                    <section key={section.key} aria-labelledby={`discovery-${section.key}`}>
                      <h3 className="h6 mb-2" id={`discovery-${section.key}`}>{section.title}</h3>
                      {section.players.length ? (
                        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
                          {section.players.map((player) => {
                            const id = player.id || player._id;
                            const cardPlayer = { ...player, _id: id, personal: { fullName: player.fullName, city: player.city, country: player.country }, skills: { primaryRole: player.primaryRole }, media: { profilePhoto: player.profilePhoto } };
                            return <div className="col" key={id}><PlayerCard player={cardPlayer} onClick={() => handleCardClick(id)} onView={() => handleCardClick(id)} showActions /></div>;
                          })}
                        </div>
                      ) : <p className="text-muted mb-0">Players from this part of your cricket network will appear here.</p>}
                    </section>
                  ))}
                </div>
              ) : filteredPlayers.length ? (
                <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
                  {filteredPlayers.map((player) => {
                    const playerId = player._id || player.id || player.personal?.fullName;
                    return (
                      <div className="col" key={playerId}>
                        <PlayerCard player={player} onClick={() => handleCardClick(playerId)} onView={() => handleCardClick(playerId)} showActions />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted mb-0">No players found.</p>
              )}

              {statusMessage && <div className="alert alert-info mb-0">{statusMessage}</div>}
            </div>
          </div>
        </div>
      ) : (
        <div className="col-12 d-flex flex-column gap-3">
          <div className="card shadow-sm app-card">
            <div className="card-body d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
              <div>
                <p className="eyebrow text-uppercase text-primary fw-semibold mb-1 small">Player detail</p>
                <h2 className="h5 mb-1">{detailTitle}</h2>
                <p className="text-muted mb-0">{detailDescription}</p>
              </div>
              <div className="d-flex flex-wrap gap-2">
                {isLoadingDetail && <span className="badge text-bg-secondary">Loading details...</span>}
                {token && (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleRecomputeStats}
                    disabled={isRecomputingStats || isLoadingDetail}
                  >
                    {isRecomputingStats ? 'Recomputing stats...' : 'Recompute stats'}
                  </button>
                )}
                <button type="button" className="btn btn-outline-secondary" onClick={handleReturnToList}>
                  Back to directory
                </button>
              </div>
            </div>
          </div>

          {statusMessage && <div className="alert alert-info mb-0">{statusMessage}</div>}
          {ratingPrivacyMessage && <div className="alert alert-success py-2 mb-0">{ratingPrivacyMessage}</div>}

          <PlayerPreview
            profile={selectedPlayer}
            socialStats={{
              visitsCount: 0,
              followersCount: followData.followersCount,
              followingCount: followData.followingCount,
            }}
            ratings={{
              isOwner: isOwnerView,
              receivedAvg: playerRating.averageRating,
              receivedCount: playerRating.ratingsCount,
              myRating: playerRating.myRating,
              receivedBreakdown: captainRating.eligible
                ? {
                    playerAvg: playerRating.averageRating,
                    playerCount: playerRating.ratingsCount,
                    captainAvg: captainRating.averageRating,
                    captainCount: captainRating.ratingsCount,
                  }
                : null,
            }}
            ratingActions={{
              showEditor: showRatingEditor,
              onOpenEditor: token && !isOwnerView ? () => setShowRatingEditor((prev) => !prev) : null,
              onRate: (rating) => submitRating('player', rating),
            }}
            socialLoading={isLoadingSocial}
            headerAction={
              user?._id !== selectedPlayer?.user && token ? (
                <button type="button" className={`btn btn-sm ${followData.isFollowing ? 'btn-outline-secondary' : 'btn-primary'}`} onClick={toggleFollow}>
                  {followData.isFollowing ? 'Unfollow' : 'Follow'}
                </button>
              ) : null
            }
          />
        </div>
      )}
    </div>
  );
};

export default PlayersDirectory;
